import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { BROADCAST_TEMPLATES } from "./broadcastTemplates";

export default function AdminBroadcastCard() {
  const [audience, setAudience] = useState("all");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => { load(); }, []);
  const load = async () => {
    try { const r = await api.get("/admin/broadcasts"); setHistory(r.data || []); } catch (e) { /* noop */ }
  };

  const loadTemplate = (tpl) => {
    const origin = window.location.origin;
    setSubject(tpl.subject);
    setBody((tpl.body_html || "").replaceAll("{{app_origin}}", origin));
    setAudience(tpl.audience || "all");
    toast.success(`Loaded "${tpl.label}" template — edit before sending`);
  };

  const send = async (dry) => {
    if (!subject.trim() || !body.trim()) { toast.error("Subject and body required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/broadcast", { audience, subject, body_html: body, dry_run: dry });
      if (dry) {
        toast.success(`Dry run: ${r.data.would_send} recipient(s)`);
      } else {
        toast.success(`Queued ${r.data.queued} send(s)`);
        setSubject(""); setBody(""); load();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="hc-card p-6" data-testid="admin-broadcast-card">
      <h3 className="font-display text-xl mb-2 flex items-center gap-2"><Send className="w-5 h-5 text-[#3A5A4A]" /> Send a broadcast email</h3>
      <p className="text-sm text-[#7A736E] mb-4">
        Compose a one-time email. Links are auto-tracked. HTML allowed in body. Always dry-run first.
      </p>

      {/* Pre-authored templates */}
      <div className="mb-4 pb-4 border-b border-[#EBE5DF]">
        <p className="text-xs uppercase tracking-wider text-[#7A736E] mb-2">Quick-start templates</p>
        <div className="flex flex-wrap gap-2">
          {BROADCAST_TEMPLATES.map((tpl) => (
            <button
              key={tpl.key}
              onClick={() => loadTemplate(tpl)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm bg-[#F3EFEA] hover:bg-[#E8DCC8] text-[#3A5A4A] transition border border-[#EBE5DF]"
              data-testid={`bc-tpl-${tpl.key}`}
              title={tpl.description}
            >
              <Sparkles className="w-3.5 h-3.5" />
              {tpl.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex gap-1.5 flex-wrap">
          {[{ v: "free", l: "Free only" }, { v: "paid", l: "Paid only" }, { v: "all", l: "All users" }].map((opt) => (
            <button key={opt.v} onClick={() => setAudience(opt.v)} className={`px-4 py-2 rounded-full text-sm transition ${audience === opt.v ? "bg-[#3A5A4A] text-white" : "bg-[#F3EFEA] text-[#7A736E] hover:bg-[#EBE5DF]"}`} data-testid={`bc-aud-${opt.v}`}>{opt.l}</button>
          ))}
        </div>
        <Input placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="rounded-xl border-[#EBE5DF]" data-testid="bc-subject" />
        <textarea placeholder="<p>Hi,</p>" value={body} onChange={(e) => setBody(e.target.value)} rows={8} className="w-full px-4 py-3 rounded-xl border border-[#EBE5DF] focus:outline-none focus:border-[#3A5A4A] text-sm font-mono" data-testid="bc-body" />
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => send(true)} disabled={busy} variant="outline" className="rounded-full border-[#EBE5DF]" data-testid="bc-dry">Dry run</Button>
          <Button onClick={() => send(false)} disabled={busy} className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538]" data-testid="bc-send"><Send className="w-4 h-4 mr-1.5" /> Send now</Button>
        </div>
      </div>
      {history.length > 0 && (
        <div className="mt-6 pt-4 border-t border-[#EBE5DF]">
          <p className="text-xs uppercase tracking-wider text-[#7A736E] mb-2">Recent broadcasts</p>
          <ul className="space-y-1.5">
            {history.slice(0, 8).map((b) => (
              <li key={b.id} className="text-xs text-[#7A736E] flex justify-between gap-2">
                <span className="truncate"><strong className="text-[#3D3935]">{b.subject}</strong> → {b.audience}</span>
                <span className="whitespace-nowrap">{b.recipient_count} sent · {new Date(b.created_at).toLocaleDateString()}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
